import { SalesforceField } from '@/model/SalesforceField';
import { FormEvent, useEffect, useState } from 'react';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';

const SalesforceDateInput = ({ fields, stepName, formData }: SalesforceField) => {
  const { updateFormData, updateSummary } = useFormContext();
  const fieldId = fields.salesforceFieldId.value;
  const inputValue = formData.records[0][fieldId] || '';

  const [dateFields, setDateFieldsData] = useState({
    dateField1: {
      value: inputValue.split('-')[0] || '',
      error: false,
      errorMessage: '',
      validation: { min: 1, max: 31 },
    },
    dateField2: {
      value: inputValue.split('-')[1] || '',
      error: false,
      errorMessage: '',
      validation: { min: 1, max: 12 },
    },
    dateField3: {
      value: inputValue.split('-')[2] || '',
      error: false,
      errorMessage: '',
      validation: { min: 1900, max: new Date().getFullYear() },
    },
  });

  const handleChange = (event: FormEvent<HTMLInputElement>, fieldName: keyof typeof dateFields) => {
    setDateFieldsData((prevState) => ({
      ...prevState,
      [fieldName]: { ...prevState[fieldName], value: (event.target as HTMLInputElement).value },
    }));
  };
  const checkError = (event: FormEvent<HTMLInputElement>, field: keyof typeof dateFields) => {
    const inputElement = event.target as HTMLInputElement;
    switch (true) {
      case inputElement.validity.valueMissing:
        setDateFieldsData((prevState) => ({
          ...prevState,
          [field]: {
            ...prevState[field],
            error: true,
            errorMessage: `Please enter the ${fields.title.value}`,
          },
        }));
        break;
      case inputElement.validity.rangeUnderflow:
        setDateFieldsData((prevState) => ({
          ...prevState,
          [field]: {
            ...prevState[field],
            error: true,
            errorMessage: inputElement.validationMessage,
          },
        }));
        break;
      case inputElement.validity.rangeOverflow:
        setDateFieldsData((prevState) => ({
          ...prevState,
          [field]: {
            ...prevState[field],
            error: true,
            errorMessage: inputElement.validationMessage,
          },
        }));
        break;
      default:
        setDateFieldsData((prevState) => ({
          ...prevState,
          [field]: { ...prevState[field], error: false },
        }));
        break;
    }
  };

  useEffect(() => {
    const dateData = `${dateFields.dateField1.value}-${dateFields.dateField2.value}-${dateFields.dateField3.value}`;
    updateFormData({
      fieldName: fields.salesforceFieldId?.value,
      value: dateData,
    });
    updateSummary({
      stepName: stepName,
      fieldName: {
        storeName: fields.salesforceFieldId?.value,
        displayName: fields.title.value,
      },
      fieldValue: dateData,
    });
  }, [
    dateFields,
    fields.salesforceFieldId?.value,
    fields.title.value,
    stepName,
    updateFormData,
    updateSummary,
  ]);

  return (
    <div className="mb-3" style={{ maxWidth: 480 }}>
      <p className="mb-1.5 form-label">
        {fields.title.value} {fields.isRequired.value === true ? '*' : ''}
      </p>
      <p className="mb-2 field-hint">For example, 01 10 1990.</p>
      <section className="date-input">
        {Object.keys(dateFields).map((fieldKey: keyof typeof dateFields) => (
          <div
            key={fieldKey}
            className={`d-flex flex-column form-element ${
              dateFields[fieldKey].error ? 'form-element--error' : ''
            }`}
          >
            <span className="input-box">
              <input
                type="number"
                id={fields.salesforceFieldId.value}
                data-id={fields.salesforceFieldId.value}
                required={fields.isRequired.value}
                min={dateFields[fieldKey].validation.min}
                max={dateFields[fieldKey].validation.max}
                className="field-text"
                value={dateFields[fieldKey].value}
                onInput={(event) => handleChange(event, fieldKey)}
                onInvalid={(event) => checkError(event, fieldKey)}
                onBlur={(event) => checkError(event, fieldKey)}
              />
              {dateFields[fieldKey].error && (
                <span className="input-icon input-icon--right input-icon--error">
                  <i className="bi bi-exclamation-triangle" />
                </span>
              )}
            </span>
            {dateFields[fieldKey].error && (
              <span className="form-element__error field-error-text text-error-400">
                {dateFields[fieldKey].errorMessage}
              </span>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};

export default SalesforceDateInput;
